import { motion, useInView } from "framer-motion";
import { useRef, useEffect } from "react";
import { useLang } from "./LangContext";

export default function About() {
  const { t } = useLang();
  const ref = useRef(null);
  const videoRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const videoInView = useInView(videoRef, { once: false, margin: "0px" });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (videoInView) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [videoInView]);

  const stats = [
    { value: "5+", label: t.about.stats.experience },
    { value: "100%", label: t.about.stats.satisfaction },
  ];

  return (
    <section id="apropos" data-testid="about-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] bg-[#0F172A]">
              <video
                ref={videoRef}
                muted
                loop
                playsInline
                preload="auto"
                className="w-full h-full object-cover"
              >
                <source src="/about.mp4" type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex gap-8">
                  {stats.map((stat, i) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ delay: 0.5 + i * 0.2 }}
                    >
                      <p className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                        {stat.value}
                      </p>
                      <p className="text-white/50 text-xs mt-1">{stat.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <span className="text-[#73B5E2] font-medium text-sm uppercase tracking-widest">
              {t.about.label}
            </span>
            <h2
              data-testid="about-title"
              className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900"
              style={{ fontFamily: 'Outfit' }}
            >
              {t.about.title}
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">{t.about.p1}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">{t.about.p2}</p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
              className="mt-8"
            >
              <button
                onClick={() => document.querySelector("#portfolio")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#73B5E2] text-white hover:bg-[#5AA0D0] rounded-full px-6 py-3 text-sm font-medium transition-all hover:scale-105 shadow-lg shadow-blue-500/20"
              >
                {t.hero.cta1}
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
